import SignClient from '@walletconnect/sign-client'
import {SessionTypes} from '@walletconnect/types'
import {NetworkType, Method} from '@/libs/wallet-connect-sdk-core/types'

let signClientInstance: SignClient

export class NeonWalletAdapter {
  signClient: SignClient | null = null
  session: SessionTypes.Struct | null = null

  network: NetworkType
  methods: Method[]

  constructor(network: NetworkType, methods: Method[]) {
    this.network = network
    this.methods = methods
  }

  isInstalled() {
    return true
  }

  isConnected(): boolean {
    return !!this.session
  }

  async init(onDelete: () => void): Promise<void> {
    if (signClientInstance) {
      this.signClient = signClientInstance
    } else {
      this.signClient = await SignClient.init({
        projectId: '8b536aaae30c4232e8f4cf3f4617499d',
        relayUrl: 'wss://relay.walletconnect.com',
        metadata: {
          name: 'nDapp',
          description:
            'Your source of information and data for applications and development groups within the Neo blockchain ecosystem.',
          url: 'https://ndapp.org/',
          icons: ['https://ndapp.org/dist/img/ndapp-loader.png'],
        },
      })
      signClientInstance = this.signClient
    }

    this.signClient.on('session_delete', async event => {
      if (this.session?.topic !== event.topic) {
        return
      }

      this.session = null

      onDelete()
    })

    for (const session of this.signClient.session.values) {
      if (session.namespaces[this.network.split(':')[0]]) {
        this.session = session
        break
      }
    }
  }

  async connect(): Promise<{
    uri?: string
    approval: () => Promise<void>
  }> {
    if (!this.signClient) {
      throw new Error('Sign client not initialized')
    }

    if (this.methods.length === 0) {
      throw new Error('At least one method is required')
    }

    const namespace = this.network.split(':')[0]

    const {approval, uri} = await this.signClient.connect({
      requiredNamespaces: {
        [namespace]: {
          chains: [this.network],
          methods: this.methods,
          events: [],
        },
      },
    })

    const approvalWrapper = async () => {
      this.session = await approval()
    }

    return {
      approval: approvalWrapper,
      uri,
    }
  }

  async disconnect(): Promise<void> {
    if (!this.signClient) {
      throw new Error('Sign client not initialized')
    }

    if (!this.session) {
      throw new Error('No session found')
    }

    await this.signClient.disconnect({
      topic: this.session.topic,
      reason: {
        code: 5900,
        message: 'USER_DISCONNECTED',
      },
    })

    this.session = null
  }

  async sendRequest(request: any): Promise<string> {
    if (!this.signClient) {
      throw new Error('Sign client not initialized')
    }

    if (!this.session) {
      throw new Error('No session found')
    }

    return await this.signClient.request({
      topic: this.session.topic,
      chainId: this.network,
      request,
    })
  }

  getAddress(): string {
    if (!this.session) {
      throw new Error('No session found')
    }

    const namespace = this.network.split(':')[0]

    const account = this.session.namespaces[namespace].accounts[0]

    if (!account) {
      throw new Error('No account found')
    }

    return account.split(':')[2]
  }
}
